import { useEffect } from 'react'
import { useLocation } from 'react-router'

interface PageMeta {
  title: string
  description?: string
  image?: string
  noindex?: boolean
}

/** Keep one set of metadata across client navigation. Never pass private media. */
export function usePageMeta({ title, description = '', image, noindex = false }: PageMeta) {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = title.includes('TryOnMe') ? title : `${title} | TryOnMe`
    const canonical = new URL(pathname, window.location.origin).href
    const setMeta = (attribute: 'name' | 'property', key: string, value?: string) => {
      const selector = `meta[${attribute}="${key}"]`
      const existing = document.head.querySelector<HTMLMetaElement>(selector)
      if (!value) {
        existing?.remove()
        return
      }
      const node = existing ?? document.createElement('meta')
      node.setAttribute(attribute, key)
      node.content = value
      if (!existing) document.head.append(node)
    }
    setMeta('name', 'description', description)
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow')
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (noindex) link?.remove()
    else {
      if (!link) {
        link = document.createElement('link')
        link.rel = 'canonical'
        document.head.append(link)
      }
      link.href = canonical
    }
    const publicImage = !noindex && image ? new URL(image, window.location.origin).href : undefined
    for (const [key, value] of Object.entries({
      'og:type': 'website', 'og:site_name': 'TryOnMe', 'og:title': document.title,
      'og:description': description, 'og:url': canonical, 'og:image': publicImage,
      'og:image:alt': publicImage ? title : undefined,
    })) setMeta('property', key, noindex ? undefined : value)
    for (const [key, value] of Object.entries({
      'twitter:card': publicImage ? 'summary_large_image' : 'summary',
      'twitter:title': document.title, 'twitter:description': description, 'twitter:image': publicImage,
    })) setMeta('name', key, noindex ? undefined : value)
    // Structured data in the initial document describes that URL only.
    document.querySelectorAll('script[data-public-schema]').forEach((node) => {
      if (node.getAttribute('data-public-schema') !== pathname) node.remove()
    })
  }, [title, description, image, noindex, pathname])
}
